import { useMemo, useState } from 'react';
import {
  Brain,
  Compass,
  Crown,
  Handshake,
  Landmark,
  Leaf,
  Loader2,
  MessageCircle,
  Microscope,
  Pickaxe,
  ScrollText,
  Shield,
  Sparkles,
  Users,
  X,
} from 'lucide-react';
import {
  useAmbassadorDialogue,
  useCivilisation,
  useNameEmpire,
  useStartCivilisationTask,
} from '../../hooks/useCivilisation';
import type { CivilisationPersonality, CivilisationTaskType } from '../../api/civilisation';
import { CreatureAvatar } from '../creatures/CreaturePanel';

interface CivilisationPanelProps {
  onClose?: () => void;
  spaceAge?: boolean;
}

type StatKey = keyof CivilisationPersonality;

const TASKS: Array<{
  type: CivilisationTaskType;
  label: string;
  icon: typeof Compass;
  color: string;
  effect: string;
}> = [
  { type: 'research', label: 'Research', icon: Microscope, color: 'text-violet-300 border-violet-800/50 bg-violet-950/30', effect: '+intelligence +tech' },
  { type: 'conserve', label: 'Conserve', icon: Leaf, color: 'text-emerald-300 border-emerald-800/50 bg-emerald-950/30', effect: '+life -pollution' },
  { type: 'trade', label: 'Trade', icon: Handshake, color: 'text-amber-300 border-amber-800/50 bg-amber-950/30', effect: '+trade +population' },
  { type: 'defend', label: 'Defend', icon: Shield, color: 'text-red-300 border-red-800/50 bg-red-950/30', effect: '+war +stability' },
  { type: 'culture', label: 'Culture', icon: ScrollText, color: 'text-fuchsia-300 border-fuchsia-800/50 bg-fuchsia-950/30', effect: '+spirit +diplomacy' },
];

const PERSONALITY_LABELS: Record<StatKey, { label: string; icon: typeof Brain; color: string }> = {
  intelligence: { label: 'Intelligence', icon: Brain, color: 'bg-violet-400' },
  war: { label: 'War', icon: Shield, color: 'bg-red-400' },
  trade: { label: 'Trade', icon: Handshake, color: 'bg-amber-400' },
  exploration: { label: 'Exploration', icon: Compass, color: 'bg-sky-400' },
  conservation: { label: 'Conservation', icon: Leaf, color: 'bg-emerald-400' },
  spirituality: { label: 'Spirituality', icon: Sparkles, color: 'bg-fuchsia-400' },
  industry: { label: 'Industry', icon: Pickaxe, color: 'bg-orange-400' },
  diplomacy: { label: 'Diplomacy', icon: Landmark, color: 'bg-cyan-400' },
};

const PROMPTS = [
  { id: 'status', label: 'Status' },
  { id: 'future', label: 'Future' },
  { id: 'planet', label: 'Planet' },
  { id: 'religion', label: 'Belief' },
];

const SCORE_LABELS: Record<string, string> = {
  development: 'Development',
  exploration: 'Exploration',
  explorer: 'Exploration',
  trade: 'Trade',
  peace: 'Peace',
  defence: 'Defence',
  research: 'Research',
  dominion: 'Dominion',
};

function formatPercent(value: number | undefined) {
  return `${Math.round(value || 0)}%`;
}

function sortedEntries(values: Record<string, number>) {
  return Object.entries(values || {})
    .sort((a, b) => b[1] - a[1])
    .slice(0, 4);
}

function PersonalityBar({ statKey, value }: { statKey: StatKey; value: number }) {
  const cfg = PERSONALITY_LABELS[statKey];
  const Icon = cfg.icon;

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-xs">
        <span className="flex items-center gap-1.5 text-slate-300">
          <Icon className="w-3.5 h-3.5 text-slate-500" />
          {cfg.label}
        </span>
        <span className="font-mono text-slate-400">{formatPercent(value)}</span>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-slate-800">
        <div className={`h-full rounded-full ${cfg.color}`} style={{ width: `${Math.max(0, Math.min(100, value))}%` }} />
      </div>
    </div>
  );
}

export default function CivilisationPanel({ onClose, spaceAge = false }: CivilisationPanelProps) {
  const { data, isPending } = useCivilisation();
  const startTask = useStartCivilisationTask();
  const nameEmpire = useNameEmpire();
  const dialogue = useAmbassadorDialogue();
  const [empireName, setEmpireName] = useState('');
  const [confirmedNameLock, setConfirmedNameLock] = useState(false);
  const [dialogueText, setDialogueText] = useState('');

  const civData = data?.data;
  const civ = civData?.civilisation;
  const lore = civData?.lore || {};
  const activeTask = civ?.tasks?.active;
  const personality = civ?.personality || ({} as Partial<CivilisationPersonality>);
  const dominantPersonality = useMemo(() => {
    const entries = Object.entries(personality) as Array<[StatKey, number]>;
    return entries.sort((a, b) => b[1] - a[1])[0]?.[0] || 'intelligence';
  }, [personality]);

  const handleDialogue = async (promptId: string) => {
    const result = await dialogue.mutateAsync(promptId);
    setDialogueText(result.data?.message || '');
  };

  if (isPending) {
    return (
      <div className="flex min-h-full items-center justify-center p-6 text-slate-400">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        Loading civilisation...
      </div>
    );
  }

  if (!civData?.unlocked || !civ) {
    return (
      <div className="min-h-full p-4">
        <div className="mb-4 flex items-center justify-between md:hidden">
          <div className="flex items-center gap-2 text-sm font-semibold">
            <Landmark className="h-4 w-4 text-cyan-300" />
            Civilisation
          </div>
          {onClose && (
            <button onClick={onClose} className="rounded-lg bg-slate-800 p-2 hover:bg-slate-700" title="Close civilisation panel">
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <div className="rounded-lg border border-slate-800 bg-slate-900/70 p-4">
          <div className="flex items-center gap-2 text-sm font-semibold text-cyan-300">
            <Landmark className="h-4 w-4" />
            Civilisation Locked
          </div>
          <p className="mt-2 text-sm text-slate-400">
            {civData?.unlockReason || 'Complete Early Tribe or Early Settlement to begin civilisation management.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full space-y-4 p-4">
      <div className="sticky top-0 z-10 -mx-4 -mt-4 flex items-center justify-between border-b border-slate-800 bg-slate-900/95 px-4 py-3 backdrop-blur md:static md:mx-0 md:mt-0 md:border-0 md:bg-transparent md:p-0">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-sm font-semibold text-cyan-300">
            <Landmark className="h-4 w-4" />
            Civilisation
          </div>
          <div className="mt-0.5 truncate text-xs text-slate-500">
            Day {civData.currentDay} - {PERSONALITY_LABELS[dominantPersonality].label} leaning
          </div>
        </div>
        {onClose && (
          <button onClick={onClose} className="rounded-lg bg-slate-800 p-2 hover:bg-slate-700" title="Close civilisation panel">
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      <section className="rounded-lg border border-cyan-800/40 bg-cyan-950/20 p-3">
        <div className="flex items-center gap-3">
          {civ.ambassador ? (
            <CreatureAvatar
              type={civ.ambassador.baseSpecies}
              variant={spaceAge ? 'spaceAmbassador' : 'ambassador'}
              className="w-20 h-20 rounded-xl border border-cyan-700/40 shadow-lg shadow-cyan-950/50"
            />
          ) : (
            <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-xl border border-cyan-700/50 bg-slate-950 text-cyan-300">
              <Users className="h-8 w-8" />
            </div>
          )}
          <div className="min-w-0">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-cyan-400/80">
              {spaceAge ? 'Space Ambassador' : 'Ambassador'}
            </div>
            <div className="text-sm font-semibold text-slate-100">
              {civ.ambassador?.evolvedTitle || 'Ambassador Emerging'}
            </div>
            <div className="mt-0.5 text-xs text-slate-400">
              {civ.ambassador
                ? `${civ.ambassador.trait} envoy evolved from your dominant ${civ.ambassador.baseSpecies}.`
                : 'Complete Intelligent Creature to reveal the first ambassador.'}
            </div>
          </div>
        </div>
        {civ.ambassador && (
          <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
            {PROMPTS.map((prompt) => (
              <button
                key={prompt.id}
                onClick={() => handleDialogue(prompt.id)}
                disabled={dialogue.isPending}
                className="flex items-center justify-center gap-1.5 rounded-lg border border-slate-700 bg-slate-900 px-2 py-1.5 text-slate-300 hover:bg-slate-800 disabled:opacity-60"
              >
                <MessageCircle className="h-3.5 w-3.5" />
                {prompt.label}
              </button>
            ))}
          </div>
        )}
        {lore.ambassador?.text && (
          <div className="mt-3 rounded-lg border border-cyan-900/50 bg-slate-950/80 p-2 text-xs leading-relaxed text-slate-300">
            {lore.ambassador.text}
          </div>
        )}
        {dialogueText && (
          <div className="mt-3 rounded-lg border border-slate-800 bg-slate-950/70 p-2 text-xs leading-relaxed text-slate-300">
            {dialogueText}
          </div>
        )}
      </section>

      <section className="rounded-lg border border-slate-800 bg-slate-900/70 p-3">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-100">
          <Crown className="h-4 w-4 text-amber-300" />
          Empire
        </div>
        {civ.empire?.locked ? (
          <div className="mt-2 rounded-lg bg-slate-800/60 px-3 py-2">
            <div className="text-sm font-semibold text-amber-200">{civ.empire.name}</div>
            <div className="mt-0.5 text-xs text-slate-500">Named on day {civ.empire.namedAt}</div>
            {lore.empire?.text && (
              <div className="mt-2 border-t border-slate-700 pt-2 text-xs leading-relaxed text-slate-300">
                {lore.empire.text}
              </div>
            )}
          </div>
        ) : (
          <form
            className="mt-3 space-y-2"
            onSubmit={(event) => {
              event.preventDefault();
              if (confirmedNameLock) nameEmpire.mutate(empireName);
            }}
          >
            <input
              value={empireName}
              onChange={(event) => setEmpireName(event.target.value)}
              className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 outline-none focus:border-amber-400"
              placeholder="Name the empire"
              maxLength={32}
            />
            <label className="flex gap-2 rounded-lg border border-amber-900/50 bg-amber-950/20 p-2 text-xs text-amber-100">
              <input
                type="checkbox"
                checked={confirmedNameLock}
                onChange={(event) => setConfirmedNameLock(event.target.checked)}
                className="mt-0.5"
              />
              This name is permanent and carries through the whole game.
            </label>
            <button
              type="submit"
              disabled={!confirmedNameLock || empireName.trim().length < 3 || nameEmpire.isPending}
              className="w-full rounded-lg bg-amber-600 px-3 py-2 text-sm font-semibold text-white hover:bg-amber-500 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Lock Empire Name
            </button>
          </form>
        )}
      </section>

      <section className="rounded-lg border border-slate-800 bg-slate-900/70 p-3">
        <div className="flex items-center justify-between text-sm font-semibold text-slate-100">
          <span className="flex items-center gap-2">
            <Landmark className="h-4 w-4 text-cyan-300" />
            Score
          </span>
          <span className="font-mono text-amber-200">{Math.round(civData.score || 0)}</span>
        </div>
        {civData.scoreBreakdown && Object.keys(civData.scoreBreakdown).length > 0 && (
          <div className="mt-2 grid grid-cols-2 gap-2 text-[11px]">
            {sortedEntries(civData.scoreBreakdown)
              .filter(([key]) => key !== 'eventCount')
              .map(([key, value]) => (
                <div key={key} className="flex items-center justify-between rounded bg-slate-950/70 px-2 py-1 text-slate-300">
                  <span>{SCORE_LABELS[key] || key}</span>
                  <span className="font-mono text-amber-200">{Math.round(value)}</span>
                </div>
              ))}
          </div>
        )}
        {civData.recentScoreEvents && civData.recentScoreEvents.length > 0 && (
          <div className="mt-2 space-y-1 text-[11px]">
            {civData.recentScoreEvents.slice(0, 3).map((event, index) => (
              <div key={`${event.reason}-${index}`} className="flex items-center justify-between gap-2 rounded bg-slate-950/70 px-2 py-1 text-slate-300">
                <span className="truncate">{event.reason}</span>
                <span className="font-mono text-green-300">+{Math.round(event.points)}</span>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="rounded-lg border border-slate-800 bg-slate-900/70 p-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-100">
            <Compass className="h-4 w-4 text-sky-300" />
            Tasks
          </div>
          {activeTask && (
            <span className="rounded-full bg-slate-800 px-2 py-0.5 text-[10px] text-slate-300">
              {activeTask.remainingDays}d left
            </span>
          )}
        </div>
        {activeTask && (
          <div className="mt-2 rounded-lg border border-sky-800/40 bg-sky-950/20 px-3 py-2 text-xs text-sky-100">
            {activeTask.label} is active. One civilisation task can run at a time.
          </div>
        )}
        <div className="mt-3 grid grid-cols-2 gap-2">
          {TASKS.map((task) => {
            const Icon = task.icon;
            return (
              <button
                key={task.type}
                onClick={() => startTask.mutate(task.type)}
                disabled={!!activeTask || startTask.isPending}
                className={`rounded-lg border p-2 text-left transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-45 ${task.color}`}
              >
                <div className="flex items-center gap-1.5 text-xs font-semibold">
                  <Icon className="h-3.5 w-3.5" />
                  {task.label}
                </div>
                <div className="mt-1 text-[10px] text-slate-400">{task.effect}</div>
              </button>
            );
          })}
        </div>
      </section>

      <section className="rounded-lg border border-slate-800 bg-slate-900/70 p-3">
        <div className="flex items-center justify-between text-sm font-semibold text-slate-100">
          <span>Personality</span>
          <span className="font-mono text-xs text-slate-500">0-100</span>
        </div>
        <div className="mt-3 space-y-3">
          {(Object.keys(PERSONALITY_LABELS) as StatKey[]).map((key) => (
            <PersonalityBar key={key} statKey={key} value={personality[key] || 0} />
          ))}
        </div>
      </section>

      <section className="rounded-lg border border-slate-800 bg-slate-900/70 p-3">
        <div className="grid grid-cols-2 gap-2 text-xs">
          <div className="rounded-lg bg-slate-800/60 p-2">
            <div className="text-slate-500">Population</div>
            <div className="mt-1 font-mono text-lg text-slate-100">{formatPercent(civData.stats.population)}</div>
          </div>
          <div className="rounded-lg bg-slate-800/60 p-2">
            <div className="text-slate-500">Technology</div>
            <div className="mt-1 font-mono text-lg text-slate-100">{formatPercent(civData.stats.technology)}</div>
          </div>
        </div>
        <div className="mt-3 rounded-lg border border-fuchsia-800/40 bg-fuchsia-950/20 p-2 text-xs">
          <div className="font-semibold text-fuchsia-200">Religion</div>
          <div className="mt-1 text-slate-300">
            {civ.religion ? `${civ.religion.name} formed from ${civ.religion.basis}.` : 'No formal religion yet.'}
          </div>
          {lore.religion?.text && (
            <div className="mt-2 border-t border-fuchsia-900/40 pt-2 text-slate-300">
              {lore.religion.text}
            </div>
          )}
        </div>
      </section>

      {lore.civilisation?.text && (
        <section className="rounded-lg border border-slate-800 bg-slate-900/70 p-3">
          <div className="text-sm font-semibold text-slate-100">Backstory</div>
          <div className="mt-2 text-xs leading-relaxed text-slate-300">{lore.civilisation.text}</div>
        </section>
      )}

      <section className="rounded-lg border border-slate-800 bg-slate-900/70 p-3">
        <div className="text-sm font-semibold text-slate-100">Future Signals</div>
        <div className="mt-2 grid grid-cols-1 gap-2 text-xs">
          <div className="rounded-lg bg-slate-800/50 p-2">
            <div className="font-semibold text-cyan-200">Likely Laws</div>
            <div className="mt-1 flex flex-wrap gap-1">
              {sortedEntries(civ.lawBiases).map(([key, value]) => (
                <span key={key} className="rounded bg-slate-900 px-1.5 py-0.5 text-slate-300">
                  {key}: {Math.round(value)}
                </span>
              ))}
            </div>
          </div>
          <div className="rounded-lg bg-slate-800/50 p-2">
            <div className="font-semibold text-red-200">Problem Risks</div>
            <div className="mt-1 flex flex-wrap gap-1">
              {sortedEntries(civ.problemRisks).map(([key, value]) => (
                <span key={key} className="rounded bg-slate-900 px-1.5 py-0.5 text-slate-300">
                  {key}: {Math.round(value)}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {civ.tasks.history.length > 0 && (
        <section className="rounded-lg border border-slate-800 bg-slate-900/70 p-3">
          <div className="text-sm font-semibold text-slate-100">Recent Direction</div>
          <div className="mt-2 space-y-1.5">
            {civ.tasks.history.slice(0, 4).map((task) => (
              <div key={task.taskId} className="flex items-center justify-between rounded bg-slate-800/50 px-2 py-1.5 text-xs">
                <span className="text-slate-300">{task.label}</span>
                <span className="text-slate-500">Day {task.completedDay}</span>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
